import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import {
  JsonApiManyResourcesDocument,
  JsonApiOneResourceDocument,
} from 'json-api';
import {
  defaultIfEmpty,
  defer,
  forkJoin,
  iif,
  mapTo,
  Observable,
  of,
  Subject,
  tap,
} from 'rxjs';
import {
  AtomParams,
  AtomService,
  SaveData,
  UpdateData,
} from '../core/atom.service';
import { ExportParams, ExportService } from '../core/export.service';
import { Country } from '../core/resources/country.resource';
import { PhoneFormat } from '../core/resources/phone-format.resource';

@Injectable({
  providedIn: 'root',
})
export class CountryService {
  get saved(): Observable<Country> {
    return this.savedSubject.asObservable();
  }
  get deleted(): Observable<Country> {
    return this.deletedSubject.asObservable();
  }

  private savedSubject = new Subject<Country>();
  private deletedSubject = new Subject<Country>();

  display = (country: Country | undefined) => {
    return country?.name![this.locale] || '';
  };

  constructor(
    private atom: AtomService,
    private exports: ExportService,
    @Inject(LOCALE_ID) public readonly locale: string
  ) {}

  all(params?: AtomParams): Observable<JsonApiManyResourcesDocument<Country>> {
    return this.atom.all(Country, params);
  }

  list(params?: AtomParams): Observable<Country[]> {
    return this.atom.list(Country, params);
  }

  find(id: string, include?: string[]): Observable<Country> {
    return this.atom.find(Country, id, { include });
  }

  export(params: ExportParams): Observable<Blob> {
    return this.exports.excel('countries', params);
  }

  save(
    country: Country,
    data?: SaveData<Country>
  ): Observable<JsonApiOneResourceDocument<Country>> {
    return this.atom
      .save(country, data)
      .pipe(tap(() => this.savedSubject.next(country)));
  }

  update(
    country: Country,
    data?: UpdateData<Country>
  ): Observable<JsonApiOneResourceDocument<Country>> {
    return this.atom
      .update(country, data)
      .pipe(tap(() => this.savedSubject.next(country)));
  }

  delete(country: Country): Observable<void> {
    return this.atom
      .delete(country)
      .pipe(tap(() => this.deletedSubject.next(country)));
  }

  saveCountryPhoneFormats(
    country: Country,
    phoneFormats: Partial<PhoneFormat>[]
  ): Observable<PhoneFormat[]> {
    return forkJoin(
      phoneFormats.map(({ id, ...data }) =>
        iif(
          () => !id,
          defer(() => {
            const phoneFormat = new PhoneFormat();
            phoneFormat.country = country;
            return this.atom.save(phoneFormat, data).pipe(mapTo(phoneFormat));
          }),
          defer(() => {
            const phoneFormat = country.phoneformats!.find(
              (format) => format.id === id
            )!;
            return this.atom.update(phoneFormat, data).pipe(mapTo(phoneFormat));
          })
        )
      )
    ).pipe(
      defaultIfEmpty([] as PhoneFormat[]),
      tap((countryPhoneFormats) => (country.phoneformats = countryPhoneFormats))
    );
  }

  deleteMissingCountryPhoneFormats(
    country: Country,
    data: Partial<PhoneFormat>[]
  ): Observable<void[]> {
    if (!country.phoneformats) {
      return of([]);
    }
    const phoneFormatIds = data.map(({ id }) => id);
    const missingPhoneFormats = country.phoneformats.filter(
      ({ id }) => !phoneFormatIds.includes(id)
    );
    return forkJoin(
      missingPhoneFormats.map((format) => this.atom.delete(format))
    ).pipe(defaultIfEmpty([] as void[]));
  }
}
